import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';

// Inline-editable Title / Tagline / Eyebrow / Send-button helpers — module-
// level so React keeps a stable component identity across re-renders and
// the contentEditable focus survives an edit keystroke.
const CTitle = ({ block, onEdit, value, fallback, size = 'text-3xl md:text-4xl', light, theme }) => (
  <E block={block} onEdit={onEdit} field="title" value={value || fallback} tag="h2"
     className={`${size} font-bold leading-[1.05] tracking-tight`}
     style={{ fontFamily: theme.headingFont || 'Syne, sans-serif', color: light ? '#fff' : theme.ink }}
     placeholder="Headline" />
);
const CTagline = ({ block, onEdit, value, light, className = 'mt-3 opacity-75', theme }) => (
  <E block={block} onEdit={onEdit} field="tagline" value={value || ''} tag="p"
     className={className} style={{ color: light ? '#fff' : theme.ink }} placeholder="Tagline (optional)" />
);
const CEyebrow = ({ block, onEdit, value, fallback }) => (
  <E block={block} onEdit={onEdit} field="eyebrow" value={value || fallback} tag="p"
     className="text-xs uppercase tracking-[0.22em] opacity-70 mb-3" placeholder="Eyebrow" />
);
const CSendBtn = ({ block, onEdit, btn, fallback, rounded = 'rounded-full', theme }) => (
  <span className={`mt-5 inline-block px-6 py-3 ${rounded} text-sm font-bold uppercase tracking-wider text-white cursor-text w-fit`}
        style={{ background: theme.accent || '#2DD4BF' }}>
    <E block={block} onEdit={onEdit} field="buttons.0.text" value={btn?.text || fallback} tag="span" placeholder="Send" />
  </span>
);
const CInfoRow = ({ block, onEdit, infoField, icon, fallback, value }) => (
  <p><span aria-hidden>{icon} </span>
    <E block={block} onEdit={onEdit} field={`info.${infoField}`} value={value || ''} tag="span" placeholder={fallback} />
  </p>
);

const ContactFormFields = ({ fields, minimal = false }) => (
  <div className={`grid ${minimal ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-2 gap-3'}`}>
    {fields.map((f, i) => {
      const isMsg = (f || '').toLowerCase().includes('message');
      const isWide = isMsg || minimal;
      return isMsg ? (
        <textarea key={i} placeholder={f} rows={4} className={`${isWide ? 'md:col-span-2' : ''} w-full px-4 py-3 border border-current/20 bg-transparent rounded-lg text-sm`} />
      ) : (
        <input key={i} type={(f||'').toLowerCase().includes('email')?'email':'text'} placeholder={f} className={`${isWide ? 'md:col-span-2' : ''} w-full px-4 py-3 border border-current/20 bg-transparent rounded-lg text-sm`} />
      );
    })}
  </div>
);

/* ════════════════════════════════════════════════════════════════════════
 * NEW BLOCK TYPE — contact (10+ variants from Figma "Contacts" page)
 *
 * Iter 175 — Section Audit. title · tagline · eyebrow · button label and
 * info.address/phone/email are all inline-editable when `E.onEdit` is
 * wired through the premium dispatch chain.
 * ════════════════════════════════════════════════════════════════════════ */
export function ContactBlock({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const layout = block.layout || 'centered-card';
  const fields = c.fields || ['Name', 'Email', 'Subject', 'Message'];
  const info = c.info || {
    address: '123 Main St, City',
    phone: '+1 (555) 123-4567',
    email: 'hello@example.com',
  };
  const buttons = c.buttons || [{ text: 'Send Message' }];

  if (layout === 'photo-overlay') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative min-h-[480px] flex items-center justify-center px-6 py-16 overflow-hidden">
        {c.image_url ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-br from-violet-700 to-fuchsia-900" />}
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 text-center text-white max-w-2xl">
          <CTitle block={block} onEdit={onEdit} value={c.title} fallback="Let's Talk" size="text-5xl md:text-6xl" light theme={theme} />
          <CTagline block={block} onEdit={onEdit} value={c.tagline} light className="mt-4 opacity-90 max-w-lg mx-auto" theme={theme} />
          <div className="mt-8 flex flex-wrap justify-center gap-3 text-sm">
            <span className="px-5 py-2.5 rounded-full bg-white/15 border border-white/25 font-bold cursor-text">📞 <E block={block} onEdit={onEdit} field="info.phone" value={info.phone || ''} tag="span" placeholder="(555) 000-0000" /></span>
            <span className="px-5 py-2.5 rounded-full bg-white/15 border border-white/25 font-bold cursor-text">✉ <E block={block} onEdit={onEdit} field="info.email" value={info.email || ''} tag="span" placeholder="hello@you.com" /></span>
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'split-form-aside') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="grid grid-cols-1 md:grid-cols-2 min-h-[520px]" style={{ background: theme.bg, color: theme.ink }}>
        <div className="relative">
          {c.image_url ? <SafeImg src={c.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-indigo-700 to-violet-900" />}
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex flex-col justify-end p-10 text-white">
            <CTitle block={block} onEdit={onEdit} value={c.title} fallback="Get in Touch" light theme={theme} />
            <CTagline block={block} onEdit={onEdit} value={c.tagline} light className="mt-3 opacity-90 max-w-sm" theme={theme} />
          </div>
        </div>
        <div className="px-8 md:px-16 py-12 flex flex-col justify-center">
          <ContactFormFields fields={fields} />
          <CSendBtn block={block} onEdit={onEdit} btn={buttons[0]} fallback="Send Message" theme={theme} />
        </div>
      </section>
    );
  }

  if (layout === 'map-top') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} style={{ background: theme.bg, color: theme.ink }}>
        <div className="h-72 relative overflow-hidden bg-gray-200">
          {c.map_image ? <SafeImg src={c.map_image} alt="map" className="w-full h-full object-cover" /> : (
            <div className="w-full h-full bg-gradient-to-br from-slate-200 via-emerald-100 to-blue-200 flex items-center justify-center text-slate-500">📍 Map placeholder</div>
          )}
        </div>
        <div className="px-6 py-16 max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <CTitle block={block} onEdit={onEdit} value={c.title} fallback="Drop Us a Line" theme={theme} />
            <CTagline block={block} onEdit={onEdit} value={c.tagline} theme={theme} />
            <div className="mt-6 space-y-2 text-sm">
              <CInfoRow block={block} onEdit={onEdit} infoField="address" icon="📍" fallback="123 Main St" value={info.address} />
              <CInfoRow block={block} onEdit={onEdit} infoField="phone"   icon="📞" fallback="(555) 000-0000" value={info.phone} />
              <CInfoRow block={block} onEdit={onEdit} infoField="email"   icon="✉"  fallback="hello@you.com" value={info.email} />
            </div>
          </div>
          <div>
            <ContactFormFields fields={fields} minimal />
            <CSendBtn block={block} onEdit={onEdit} btn={buttons[0]} fallback="Send" theme={theme} />
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'chips-below') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-3xl mx-auto">
          <div className="border border-current/10 rounded-2xl p-8 md:p-12" style={{ borderRadius: theme.radius || 16 }}>
            <CTitle block={block} onEdit={onEdit} value={c.title} fallback="Get In Touch with Us" theme={theme} />
            <CTagline block={block} onEdit={onEdit} value={c.tagline} theme={theme} />
            <div className="mt-6"><ContactFormFields fields={fields} /></div>
            <CSendBtn block={block} onEdit={onEdit} btn={buttons[0]} fallback="Send Message" theme={theme} />
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="border border-current/10 rounded-lg p-4 text-sm">
              <p className="text-[10px] uppercase tracking-wider opacity-60 font-bold">Address</p>
              <E block={block} onEdit={onEdit} field="info.address" value={info.address || ''} tag="p" className="mt-1" placeholder="123 Main St" />
            </div>
            <div className="border border-current/10 rounded-lg p-4 text-sm">
              <p className="text-[10px] uppercase tracking-wider opacity-60 font-bold">Phone</p>
              <E block={block} onEdit={onEdit} field="info.phone" value={info.phone || ''} tag="p" className="mt-1" placeholder="(555) 000-0000" />
            </div>
            <div className="border border-current/10 rounded-lg p-4 text-sm">
              <p className="text-[10px] uppercase tracking-wider opacity-60 font-bold">Email</p>
              <E block={block} onEdit={onEdit} field="info.email" value={info.email || ''} tag="p" className="mt-1" placeholder="hello@you.com" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'map-right') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="grid grid-cols-1 md:grid-cols-2 min-h-[480px]" style={{ background: theme.bg, color: theme.ink }}>
        <div className="px-8 md:px-16 py-12 flex flex-col justify-center">
          <CTitle block={block} onEdit={onEdit} value={c.title} fallback="You Can Mail Us" theme={theme} />
          <CTagline block={block} onEdit={onEdit} value={c.tagline} theme={theme} />
          <div className="mt-6 space-y-3 text-sm">
            <CInfoRow block={block} onEdit={onEdit} infoField="address" icon="📍" fallback="123 Main St" value={info.address} />
            <CInfoRow block={block} onEdit={onEdit} infoField="phone"   icon="📞" fallback="(555) 000-0000" value={info.phone} />
            <CInfoRow block={block} onEdit={onEdit} infoField="email"   icon="✉"  fallback="hello@you.com" value={info.email} />
          </div>
        </div>
        <div className="bg-gray-100">
          {c.map_image ? <SafeImg src={c.map_image} alt="map" className="w-full h-full object-cover" /> : (
            <div className="w-full h-full bg-gradient-to-br from-slate-200 via-emerald-100 to-blue-200 flex items-center justify-center text-slate-500">📍 Map placeholder</div>
          )}
        </div>
      </section>
    );
  }

  if (layout === 'dark-mini') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16 text-center text-white" style={{ background: '#3B1F8B' }}>
        <CTitle block={block} onEdit={onEdit} value={c.title} fallback="For any questions please mail us" size="text-2xl md:text-3xl" light theme={theme} />
        <p className="mt-4 text-lg">
          <span className="underline cursor-text">
            <E block={block} onEdit={onEdit} field="info.email" value={info.email || ''} tag="span" placeholder="hello@you.com" />
          </span>
        </p>
        <div className="mt-6 flex justify-center gap-3 text-sm">
          {(c.socials || ['Twitter','Instagram','LinkedIn']).map((s,i) => (
            <span key={i} className="px-4 py-1.5 rounded-full bg-white/10 border border-white/20 cursor-text">
              <E block={block} onEdit={onEdit} field={`socials.${i}`} value={s} tag="span" placeholder="Social" />
            </span>
          ))}
        </div>
      </section>
    );
  }

  if (layout === 'two-col-with-info-card') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <CEyebrow block={block} onEdit={onEdit} value={c.eyebrow} fallback="Get in Touch" />
            <CTitle block={block} onEdit={onEdit} value={c.title} fallback="You Can Mail Us" theme={theme} />
            <CTagline block={block} onEdit={onEdit} value={c.tagline} theme={theme} />
            <div className="mt-8"><ContactFormFields fields={fields} /></div>
            <CSendBtn block={block} onEdit={onEdit} btn={buttons[0]} fallback="Send" theme={theme} />
          </div>
          <div className="space-y-4">
            <div className="p-6 rounded-2xl text-white" style={{ background: '#3B1F8B', borderRadius: theme.radius || 16 }}>
              <p className="text-xs uppercase tracking-[0.18em] opacity-80 font-bold mb-3">Contact Info</p>
              <CInfoRow block={block} onEdit={onEdit} infoField="address" icon="📍" fallback="123 Main St" value={info.address} />
              <CInfoRow block={block} onEdit={onEdit} infoField="phone"   icon="📞" fallback="(555) 000-0000" value={info.phone} />
              <CInfoRow block={block} onEdit={onEdit} infoField="email"   icon="✉"  fallback="hello@you.com" value={info.email} />
            </div>
            <div className="aspect-square bg-gray-100">
              {c.map_image ? <SafeImg src={c.map_image} alt="map" className="w-full h-full object-cover" /> : (
                <div className="w-full h-full bg-gradient-to-br from-slate-200 via-emerald-100 to-blue-200 flex items-center justify-center text-slate-500">📍 Map placeholder</div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ── chat-invite · Iter 189c Phase B ─────────────────────────────────
  //  Chat-widget-inspired hero — a mocked conversation bubble stack with a
  //  reassuring headline, a real form on the right, and inline contact info
  //  chips below.
  if (layout === 'chat-invite') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || 'default'} className="px-6 py-24" style={{ background: theme.bg || '#FAF6EE', color: theme.ink || '#0E0E23' }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Left · Mocked conversation */}
          <div>
            <CEyebrow block={block} onEdit={onEdit} value={c.eyebrow} fallback="LIVE SUPPORT" />
            <CTitle block={block} onEdit={onEdit} value={c.title} fallback="Real people. Real replies." theme={theme} />
            <CTagline block={block} onEdit={onEdit} value={c.subtitle || c.tagline} className="mt-3 opacity-70" theme={theme} />

            <div className="mt-8 p-4 rounded-2xl" style={{ background: '#fff', border: `1px solid ${theme.ink || '#0E0E23'}12`, borderRadius: theme.radius || 16, boxShadow: '0 10px 30px rgba(15,15,35,0.06)' }}>
              <div className="flex items-center gap-2 pb-3 border-b" style={{ borderColor: `${theme.ink || '#0E0E23'}10` }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#22c55e' }} />
                <span className="text-xs font-bold uppercase tracking-wider opacity-70">Online · avg reply 12 min</span>
              </div>
              <div className="pt-4 space-y-3">
                <div className="max-w-[75%] px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm" style={{ background: '#F1F5F9' }}>Hi! How can we help today?</div>
                <div className="max-w-[75%] ml-auto px-4 py-2.5 rounded-2xl rounded-br-sm text-sm text-white" style={{ background: theme.accent || '#8B5CF6' }}>I&rsquo;d like a quote for a project.</div>
                <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-bl-sm text-sm" style={{ background: '#F1F5F9' }}>Wonderful — drop your details and one of us will reply within the hour.</div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3 text-sm opacity-80">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: `${theme.ink || '#0E0E23'}08` }}>
                <span aria-hidden>✉</span>
                <E block={block} onEdit={onEdit} field="info.email" value={info.email || ''} tag="span" placeholder="hello@you.com" />
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: `${theme.ink || '#0E0E23'}08` }}>
                <span aria-hidden>📞</span>
                <E block={block} onEdit={onEdit} field="info.phone" value={info.phone || ''} tag="span" placeholder="(555) 000-0000" />
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: `${theme.ink || '#0E0E23'}08` }}>
                <span aria-hidden>📍</span>
                <E block={block} onEdit={onEdit} field="info.address" value={info.address || ''} tag="span" placeholder="City · Country" />
              </span>
            </div>
          </div>

          {/* Right · Form */}
          <div className="p-8 rounded-2xl" style={{ background: '#fff', border: `1px solid ${theme.ink || '#0E0E23'}12`, borderRadius: theme.radius || 16, boxShadow: '0 20px 60px rgba(15,15,35,0.08)' }}>
            <p className="text-xs uppercase tracking-[0.22em] font-bold opacity-70">Start a conversation</p>
            <h3 className="mt-2 text-2xl font-bold" style={{ fontFamily: theme.headingFont }}>Send us a message</h3>
            <div className="mt-6"><ContactFormFields fields={fields.length ? fields : ['Your Name', 'Email', 'How can we help?', 'Message']} /></div>
            <CSendBtn block={block} onEdit={onEdit} btn={buttons[0]} fallback="Send Message" theme={theme} />
          </div>
        </div>
      </section>
    );
  }

  // centered-card (default)
  return (
    <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-2xl mx-auto text-center">
        <CEyebrow block={block} onEdit={onEdit} value={c.eyebrow} fallback="Contact" />
        <CTitle block={block} onEdit={onEdit} value={c.title} fallback="Get In Touch" theme={theme} />
        <CTagline block={block} onEdit={onEdit} value={c.tagline} className="mt-4 opacity-70" theme={theme} />
        <div className="mt-8 text-left"><ContactFormFields fields={fields} /></div>
        <CSendBtn block={block} onEdit={onEdit} btn={buttons[0]} fallback="Send Message" theme={theme} />
      </div>
    </section>
  );
}

